import classNames from 'classnames';
import React from 'react';
import sanitize from 'sanitize-html';

import { RadioGroup } from '@/design-system/atoms/Forms/RadioGroup';
import { Radio } from '@/design-system/atoms/Forms/RadioGroup/Radio/Radio';
import { SearchableCheckbox } from '@/design-system/atoms/Forms/SearchableCheckbox';
import { LazyIconSearch } from '@/design-system/atoms/Icons/IconSearch/Lazy';

import { useLocationSearchContext } from '../../../contexts/LocationSearchContext';
import styles from '../LocationFilter.module.scss';

export const LocationFacetRoot = (props) => {
    const {
        urlParamName,
        variant,
        title = 'Filters apply upon selection',
        controller,
        analytics,
    } = props;

    const { handleSingleFilter, handleMultipleFilters, setCoveoAnalytics } =
        useLocationSearchContext();

    const [state, setState] = React.useState();

    React.useEffect(() => {
        return controller.subscribe(() => setState(controller.state));
    }, [controller]);

    const handleSingleSelect = React.useCallback(
        (newValue) => {
            if (!newValue) {
                setCoveoAnalytics('facetClearAll');
                handleSingleFilter({ [urlParamName]: '' }, { ...analytics, actionLabel: 'All' });
                return;
            }

            const facetValue = state?.values?.find(({ value }) => value === newValue);
            if (!facetValue) {
                console.error('LocationFacetRoot - invalid facetValue');
                return;
            }

            setCoveoAnalytics('facetSelect', {
                facetId: state.facetId,
                facetValue: facetValue.value,
            });
            handleSingleFilter(
                {
                    [urlParamName]: `${facetValue.value};${facetValue.numberOfResults}`,
                },
                { ...analytics, actionLabel: newValue },
            );
        },
        [state, handleSingleFilter, urlParamName, analytics, setCoveoAnalytics],
    );

    const handleToggleSelect = React.useCallback(
        (newValue) => {
            const facetValue = state?.values?.find(({ value }) => value === newValue);
            if (!facetValue) {
                console.error('LocationFacetRoot - invalid facetValue');
                return;
            }

            setCoveoAnalytics(
                controller.isValueSelected(facetValue) ? 'facetDeselect' : 'facetSelect',
                { facetId: state.facetId, facetValue: facetValue.value },
            );

            handleMultipleFilters(
                {
                    [urlParamName]: `${facetValue.value};${facetValue.numberOfResults}`,
                },
                { ...analytics, actionLabel: newValue },
            );
        },
        [state, controller, handleMultipleFilters, urlParamName, analytics, setCoveoAnalytics],
    );

    const [radioValue, radioItems] = React.useMemo(() => {
        if (variant !== 'radio') {
            return [];
        }

        let value = '';
        const items = [];

        state?.values?.forEach((v) => {
            items.push(v);
            if (controller.isValueSelected(v)) {
                value = v.value;
            }
        });

        return [value, items];
    }, [variant, state?.values, controller]);

    const checkboxItems = React.useMemo(() => {
        if (variant !== 'checkbox') {
            return [];
        }

        return (
            state?.values?.map((v) => ({
                label: <span dangerouslySetInnerHTML={{ __html: sanitize(v.value) }} />,
                value: v.value,
                isSelected: controller.isValueSelected(v),
                onChange: () => handleToggleSelect(v.value),
            })) ?? []
        );
    }, [variant, state?.values, controller, handleToggleSelect]);

    if (variant == null || controller == null) {
        console.error('LocationFacetRoot: variant and controller is required');
        return null;
    }

    if (variant === 'radio') {
        return (
            <fieldset className={styles[`location-filter__fieldset`]}>
                <legend className={classNames(styles[`location-filter__legend`], 'type-t3')}>
                    {title}
                </legend>
                <RadioGroup value={radioValue} onChange={handleSingleSelect}>
                    <Radio value="" label="All" />
                    {radioItems.map(({ value }) => (
                        <Radio
                            key={value}
                            value={value}
                            label={<span dangerouslySetInnerHTML={{ __html: sanitize(value) }} />}
                        />
                    ))}
                </RadioGroup>
            </fieldset>
        );
    }

    return (
        <SearchableCheckbox
            text="Filters apply upon selection"
            label="Search"
            iconComponent={LazyIconSearch}
            items={checkboxItems}
            isScrollable={true}
            onTextFieldKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                }
            }}
        />
    );
};

export const LocationFacetBadge = (props) => {
    const { fieldName } = props;

    const {
        coveo: {
            controller: { breadcrumbManager },
        },
    } = useLocationSearchContext();

    const [state, setState] = React.useState();

    React.useEffect(() => {
        return breadcrumbManager.subscribe(() => setState(breadcrumbManager.state));
    }, [breadcrumbManager]);

    if (fieldName == null) {
        console.error('`LocationFacetBadge: fieldName is required');
        return null;
    }

    const total = state?.facetBreadcrumbs.find((fb) => fb.field === fieldName)?.values?.length ?? 0;

    if (total === 0) {
        return null;
    }

    const formattedTotal = total > 99 ? '99+' : total;

    return (
        <div className={classNames(styles[`location-filter__indicator`], 'type-t4')}>
            {formattedTotal}
        </div>
    );
};

export const LocationFacet = {
    Root: LocationFacetRoot,
    Badge: LocationFacetBadge,
};
